// Today's Doses widget — used on Home, Account, and Reminders pages.
// Compact list of doses due today with quick-action buttons.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, X, Clock, Pill, ChevronRight, Loader2, AlarmClock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";

const STATUS_DOT = {
    pending: "bg-amber-400",
    taken: "bg-emerald-500",
    delayed: "bg-emerald-400",
    skipped: "bg-rose-400",
    missed: "bg-rose-500",
    snoozed: "bg-blue-400",
};

const TodayDosesWidget = ({ compact = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get("/reminder/today");
            setData(d);
        } catch {
            // user might not be logged in; render nothing in that case
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doAction = async (dose, action, extra = {}) => {
        if (acting) return;
        try {
            setActing(dose.id);
            await api.post(`/reminder/doses/${dose.id}/action`, { action, ...extra });
            toast.success({
                taken: "Marked taken — great job!",
                skipped: "Marked skipped",
                snoozed: "Snoozed",
                delayed: "Logged",
            }[action] || "Updated");
            await load();
        } catch {
            toast.error("Could not update");
        } finally {
            setActing(null);
        }
    };

    if (loading) {
        return <div className="h-32 skeleton rounded-2xl" />;
    }
    if (!data || data.summary.total === 0) {
        return (
            <div className="rounded-2xl border border-zinc-100 bg-white p-5 text-sm text-zinc-500" data-testid="today-doses-empty">
                <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4 text-zinc-400" />
                    <span>No doses scheduled for today.</span>
                </div>
                <Link to="/reminders/add" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline">
                    Add a medicine <ChevronRight className="h-3 w-3" />
                </Link>
            </div>
        );
    }

    const items = compact ? data.items.slice(0, 4) : data.items;

    return (
        <div className="rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid="today-doses-widget">
            <header className="mb-3 flex items-end justify-between">
                <div>
                    <p className="label-uppercase">Today</p>
                    <h3 className="text-lg font-semibold text-zinc-900">
                        {data.summary.pending} pending · {data.summary.taken} taken
                    </h3>
                </div>
                <Link to="/reminders" className="text-xs font-semibold text-brand-700 hover:underline" data-testid="today-doses-view-all">
                    View all →
                </Link>
            </header>

            <ul className="space-y-2" data-testid="today-doses-list">
                {items.map((d) => {
                    const isPending = d.status === "pending";
                    const time = d.scheduled_at?.slice(11, 16);
                    return (
                        <li
                            key={d.id}
                            className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-zinc-50/40 px-3 py-2.5"
                            data-testid={`today-dose-${d.id}`}
                        >
                            <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[d.status] || "bg-zinc-300"}`} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    <p className="font-semibold text-sm text-zinc-900 truncate">{d.medicine_name || "Medication"}</p>
                                    {d.is_critical && (
                                        <span title="Critical medication" className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700">
                                            <ShieldAlert className="h-2.5 w-2.5" />
                                        </span>
                                    )}
                                </div>
                                <p className="text-[11px] text-zinc-500">
                                    <Clock className="mr-0.5 inline h-3 w-3" />
                                    {time} · {d.slot_label} · {d.dosage_quantity || d.strength || ""}
                                </p>
                            </div>
                            {isPending ? (
                                <div className="flex shrink-0 items-center gap-1">
                                    <button
                                        onClick={() => doAction(d, "taken")}
                                        disabled={acting === d.id}
                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50"
                                        data-testid={`today-dose-taken-${d.id}`}
                                    >
                                        {acting === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                        Taken
                                    </button>
                                    <button
                                        onClick={() => doAction(d, "snoozed", { snooze_minutes: 10 })}
                                        disabled={acting === d.id}
                                        className="inline-flex items-center justify-center rounded-full bg-white border border-zinc-200 px-2 py-1 text-zinc-600 hover:border-brand-300"
                                        title="Snooze 10 minutes"
                                        aria-label="Snooze 10 minutes"
                                        data-testid={`today-dose-snooze-${d.id}`}
                                    >
                                        <AlarmClock className="h-3 w-3" />
                                    </button>
                                    <button
                                        onClick={() => doAction(d, "skipped")}
                                        disabled={acting === d.id}
                                        className="inline-flex items-center justify-center rounded-full bg-white border border-zinc-200 px-2 py-1 text-zinc-500 hover:border-rose-300 hover:text-rose-600"
                                        title="Skip"
                                        aria-label="Skip"
                                        data-testid={`today-dose-skip-${d.id}`}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ) : (
                                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600 ring-1 ring-zinc-200">
                                    {d.status}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default TodayDosesWidget;
